import { useEffect, useState, type RefObject } from "react";

/**
 * Altura máxima para o elemento ocupar o resto da janela sem passar dela.
 *
 * Serve para transformar uma tabela longa num quadro de altura limitada: aí a
 * barra de rolagem horizontal fica sempre visível na base do quadro, em vez de
 * ficar no fim de uma tabela de 500 linhas — onde a pessoa só chega descendo a
 * página inteira e, ao voltar, perdeu a linha em que estava.
 *
 * Por que medir em JS em vez de `calc(100dvh - Xrem)`: o desconto não é
 * constante. A topbar de 3.5rem do DashboardLayout só existe no mobile, e a
 * barra de ferramentas acima da tabela muda de altura conforme os filtros
 * ativos. Um valor fixo erraria em quase toda combinação.
 *
 * Não recalcula no scroll de propósito: recalcular a cada frame de rolagem
 * causa thrash de layout. O efeito colateral é sobrar espaço abaixo quando a
 * página já está rolada, o que é inofensivo.
 *
 * `enabled`: quem só monta o elemento depois que os dados chegam (skeleton
 * primeiro, tabela depois) precisa avisar. O efeito lê `ref.current` uma vez;
 * sem um dep que mude na troca, ele nunca reexecuta e a altura fica indefinida
 * para sempre — a tabela volta a passar da janela.
 */
export function useFillViewportHeight(
  ref: RefObject<HTMLElement | null>,
  {
    gutter = 24,
    min = 320,
    enabled = true,
  }: { gutter?: number; min?: number; enabled?: boolean } = {},
): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof window === "undefined") return;

    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        const top = node.getBoundingClientRect().top;
        const next = Math.max(min, window.innerHeight - top - gutter);
        // Limitar a altura deste elemento muda o tamanho do pai, que é quem o
        // ResizeObserver observa — ou seja, existe realimentação. A tolerância
        // de 2px impede que uma diferença de arredondamento fique oscilando
        // entre dois valores e derrube a página com "Maximum update depth".
        setMaxHeight((prev) =>
          prev != null && Math.abs(prev - next) <= 2 ? prev : next,
        );
      });
    };

    measure();
    window.addEventListener("resize", measure);

    // A toolbar acima cresce ao abrir filtros ou ao fixar vistas; sem observar
    // o pai, a tabela passaria a ultrapassar a janela nesses casos.
    const observer = new ResizeObserver(measure);
    if (el.parentElement) observer.observe(el.parentElement);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [ref, gutter, min, enabled]);

  return maxHeight;
}
